import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import useToggle from "../../../hooks/useToggle";
import { ContextDamageForm } from "../DamageFormContext";
import SectionCardActions from "../SectionCardActions";
import SectionCardHeader from "../SectionCardHeader";
import { DamageFormTypes } from "../Types";
import useDamageFormActions from "../service/DamageFormActions";
import DamageFormService from "../service/DamageFormService";
import Dialogs from "../tasks/dialogs";
import AttachmentsTable from "./AttachmentsTable";
import GeneralInfoCard from "./GeneralInfoCard";
import PracticesTable from "./PracticesTable";
import { useHasScope } from "../../../../../config/globalConfig";

const GeneralInfo = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const { generalInfo } = useContext(ContextDamageForm);

  const t = useOperationTranslation();

  const navigate = useNavigate();
  const { id } = useParams();

  const snackBar = useSnackbar();

  const [openCloseConfirm, toggleCloseConfirm] = useToggle();
  const [reopenCloseConfirm, toggleReopenConfirm] = useToggle();
  const [changeRepoConfirm, toggleChangeRepoConfirm] = useToggle();

  const actions = useDamageFormActions();

  const form = useForm<DamageFormTypes.IGeneralInfoUpdate>();

  const onFormReset = () => {
    form.reset({
      damageFormId: generalInfo?.id,
      eventTypeId: generalInfo?.eventType.id
    });
  };

  useEffect(() => {
    onFormReset();
  }, [generalInfo]);

  const onSubmit = (data: DamageFormTypes.IGeneralInfoUpdate) => {
    setLoading(true);
    DamageFormService.details.generalInfo.put.updateByid(tenant, data).then(
      () => {
        actions.getGeneralInfo();        
		actions.getTask2();
        actions.getTask3();
        snackBar.show(
          "success",
          t("damageForm.details.generalInfo.snackbar.update.success")
        );
        setLoading(false);
      },
      (error: any) => {

        if (error.response.status == 409) {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.update.error409")
          );
        } else {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.update.error")
          );
        }
        setLoading(false);
      }
    );
  };

  const onClose = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.generalInfo.put.closeByid(id).then(
      () => {
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.generalInfo.snackbar.close.success")
        );
        toggleCloseConfirm();
        setLoading(false);
      },
      (error: any) => {
        if (error.response.status == 422) {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.close.error422")
          );
        } else {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.close.error")
          );
        }

        toggleCloseConfirm();
        setLoading(false);
      }
    );
  };

  const onChangeRepo = () => {

    if (generalInfo != null && generalInfo.id != null) {
    
      setLoading(true);
      DamageFormService.details.generalInfo.put.changeRepo(tenant, generalInfo?.id, 19).then(
        () => {
          actions.getGeneralInfo();
          snackBar.show(
            "success",
            t("damageForm.details.generalInfo.snackbar.chagerepo.success")
          );
          toggleChangeRepoConfirm();
          setLoading(false);
        },
        (error: any) => {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.chagerepo.error")
          );

          toggleChangeRepoConfirm();
          setLoading(false);
        }
      );
    }
  };

  const onReopen = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.generalInfo.put.reopenByid(tenant, id).then(
      () => {
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.generalInfo.snackbar.reopen.success")
        );
        toggleReopenConfirm();
        setLoading(false);
      },
      (error: any) => {
        snackBar.show(
          "error",
          t("damageForm.details.generalInfo.snackbar.reopen.error")
        );

        toggleReopenConfirm();
        setLoading(false);
      }
    );
  };

  const onViewMissingEquipments = () => {
    navigate(
      `/operation/damages/missingEquipments?formCode=${generalInfo?.progressive}&tenant=${generalInfo?.tenant}`
    );
  };

  const onViewDamage = () => {
    navigate(
      `/operation/damages/list?formCode=${generalInfo?.progressive}&open=true&tenant=${generalInfo?.tenant}`
    );
  };

  return (
    <>
      <Mui.Box mt={2}>
        <Mui.Card>
          <SectionCardHeader>
            <Mui.Grid container spacing={1} mr={1}>
              <Mui.Grid item xs="auto">
                <Mui.Button
                  variant="contained"
                  size="small"
                  startIcon={<MuiIcons.WarningRounded />}
                  onClick={onViewDamage}
                >
                  <Mui.Typography variant="h6" color="inherit">
                    {t("damageForm.details.generalInfo.header.btnViewDamage")}
                  </Mui.Typography>
                </Mui.Button>
              </Mui.Grid>

              <Mui.Grid item xs="auto">
                <Mui.Button
                  variant="contained"
                  size="small"
                  startIcon={<MuiIcons.WarningRounded />}
                  onClick={onViewMissingEquipments}
                >
                  <Mui.Typography variant="h6" color="inherit">
                    {t(
                      "damageForm.details.generalInfo.header.btnViewMissingEquipments"
                    )}
                  </Mui.Typography>
                </Mui.Button>
              </Mui.Grid>
			  
			  
			{generalInfo?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_UPDATE_DAMAGE_DAMAGE_FORM") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HourglassEmpty />}
                      onClick={toggleChangeRepoConfirm}
                      disabled={generalInfo?.repository.id == 19}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.generalInfo.header.btnTuvWaiting")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}


              {generalInfo?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_CLOSE_DAMAGE_DAMAGE_FORM") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleCloseConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.generalInfo.header.btnClose")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
              {!generalInfo?.open &&
                generalInfo?.migrated &&
                hasScope("SCOPE_REOPEN_DAMAGE_DAMAGE_FORM") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleReopenConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.generalInfo.header.btnReopen")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
            </Mui.Grid>
          </SectionCardHeader>

          <Mui.CardContent>
            <Mui.Stack spacing={2}>
              <GeneralInfoCard form={form} />

              <AttachmentsTable />

              <PracticesTable />
            </Mui.Stack>
          </Mui.CardContent>
          {hasScope("SCOPE_UPDATE_DAMAGE_DAMAGE_FORM_EVENT_TYPE") && (
            <SectionCardActions
              onClickCancel={onFormReset}
              onClickSave={form.handleSubmit(onSubmit)}
            />
          )}
        </Mui.Card>
      </Mui.Box>

      <Dialogs.CloseTaskConfirmation
        open={openCloseConfirm}
        onClose={toggleCloseConfirm}
        onCloseTask={onClose}
        taskName={t("damageForm.details.tabs.damageForm")}
      />

      <Dialogs.ReopenTaskConfirmation
        open={reopenCloseConfirm}
        onReopen={toggleReopenConfirm}
        onReopenTask={onReopen}
        taskName={t("damageForm.details.tabs.damageForm")}
      />
	  
	  <Dialogs.ChangeRepoConfirmation
        open={changeRepoConfirm}
        onClose={toggleChangeRepoConfirm}
        onChangeRepo={onChangeRepo}
      />

    </>
  );
};
export default GeneralInfo;
